// NOTICE: KAPTION-INTEGRATION - image-preview-race
//
// Retries a media element (img / video / audio) that failed to load before the bubble gives up and
// shows "unavailable". A just-sent attachment can be broadcast a few milliseconds before its file is
// in storage, so the first request misses; incoming media can hit the same window. Each retry waits
// longer (backoff) and, for Active Storage URLs, adds a cache-busting query param so the browser
// really asks again instead of reusing the failed response.
//
// Presigned storage URLs (S3 / R2) are never rewritten: an extra query param would break their
// signature. For those the retry re-mounts the element (bump `retryKey`) with the same URL.
import { ref, computed, watch, onBeforeUnmount } from 'vue';

export const MEDIA_RETRY_PARAM = 'kpt_retry';
export const DEFAULT_MEDIA_RETRIES = 3;
export const DEFAULT_MEDIA_RETRY_DELAY_MS = 500;

const isPresignedUrl = url =>
  /[?&](X-Amz-Signature|Signature|sig)=/i.test(url || '');

export const withRetryParam = (url, attempt) => {
  if (!url || !attempt || isPresignedUrl(url)) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set(MEDIA_RETRY_PARAM, String(attempt));
    // Keep relative URLs relative, the way the bubble received them.
    return /^https?:\/\//i.test(url)
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

/**
 * @param {import('vue').Ref<string>|(() => string)} urlSource the media URL
 * @param {Object} [options]
 * @param {number} [options.retries] retries before giving up (default 3)
 * @param {number} [options.baseDelay] first retry delay in ms; doubles each time (default 500)
 * @param {Function} [options.onGiveUp] called once when every retry failed
 * @param {Function} [options.onRetry] called with the new src after each retry is scheduled to load
 */
export function useKaptionMediaRetry(urlSource, options = {}) {
  const {
    retries = DEFAULT_MEDIA_RETRIES,
    baseDelay = DEFAULT_MEDIA_RETRY_DELAY_MS,
    onGiveUp = () => {},
    onRetry = () => {},
  } = options;

  const url = computed(() =>
    typeof urlSource === 'function' ? urlSource() : urlSource.value
  );
  const attempt = ref(0);
  const hasError = ref(false);
  const isRetrying = ref(false);
  let timer = null;

  const src = computed(() => withRetryParam(url.value, attempt.value));
  // Changes on every retry, so `:key="retryKey"` re-mounts the element.
  const retryKey = computed(() => `${url.value}#${attempt.value}`);

  const clearTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const onError = () => {
    if (hasError.value || isRetrying.value) return;
    if (attempt.value >= retries) {
      hasError.value = true;
      onGiveUp();
      return;
    }
    isRetrying.value = true;
    const delay = baseDelay * 2 ** attempt.value;
    timer = setTimeout(() => {
      timer = null;
      isRetrying.value = false;
      attempt.value += 1;
      onRetry(src.value);
    }, delay);
  };

  const reset = () => {
    clearTimer();
    attempt.value = 0;
    hasError.value = false;
    isRetrying.value = false;
  };

  watch(url, (next, prev) => {
    if (next !== prev) reset();
  });

  onBeforeUnmount(clearTimer);

  return { src, retryKey, attempt, hasError, isRetrying, onError, reset };
}
